import { type UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getGetConvertResultSettlementStatisticsExpenditureNaturesQueryKey,
  getGetConvertResultSettlementStatisticsExpenditurePurposesQueryKey,
  getGetConvertResultSettlementStatisticsRevenuePurposesQueryKey,
  usePostSettlementStatisticsExpenditureNature,
  usePostSettlementStatisticsExpenditurePurpose,
  usePostSettlementStatisticsRevenuePurpose,
} from '@/entities/settlementstatisticsconverter/api/generated/api';
import type { ErrorResponse } from '@/entities/settlementstatisticsconverter/api/generated/model/errorResponse';
import type { SuccessResponse } from '@/entities/settlementstatisticsconverter/api/generated/model/successResponse';
import type { MasterType } from '@/entities/settlementstatisticsconverter/lib/master-type';
import type { ErrorType } from '@/shared/api/custom-instance';
import { toast } from '@/shared/ui';

/**
 * マスタ種別に応じた POST mutation を返すフック
 * MasterDeleteDialog と同パターンで masterType で切り替える
 */
export const useMasterCreateMutation = ({
  resultId,
  masterType,
  onSuccess,
}: {
  resultId: string;
  masterType: MasterType;
  onSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const masterTitle = t('settlement-statistics-converter.navigation.title');

  const queryKeyMap = {
    expenditure_purpose: getGetConvertResultSettlementStatisticsExpenditurePurposesQueryKey,
    expenditure_nature: getGetConvertResultSettlementStatisticsExpenditureNaturesQueryKey,
    revenue_purpose: getGetConvertResultSettlementStatisticsRevenuePurposesQueryKey,
  } as const;

  const mutationOptions: {
    mutation: Pick<
      UseMutationOptions<SuccessResponse, ErrorType<ErrorResponse>, unknown>,
      'onSuccess' | 'onError'
    >;
  } = {
    mutation: {
      onSuccess: () => {
        toast.success(t('common.messages.create-success', { name: masterTitle }));
        queryClient.invalidateQueries({
          queryKey: queryKeyMap[masterType](resultId),
        });
        onSuccess();
      },
      onError: () => {
        toast.error(t('common.messages.create-failed', { name: masterTitle }));
      },
    },
  };

  const expenditurePurposeMutation = usePostSettlementStatisticsExpenditurePurpose(mutationOptions);
  const expenditureNatureMutation = usePostSettlementStatisticsExpenditureNature(mutationOptions);
  const revenuePurposeMutation = usePostSettlementStatisticsRevenuePurpose(mutationOptions);

  const mutationMap = {
    expenditure_purpose: expenditurePurposeMutation,
    expenditure_nature: expenditureNatureMutation,
    revenue_purpose: revenuePurposeMutation,
  } as const;

  return mutationMap[masterType];
};
